import PrimaryButton from '@/components/Buttons/PrimaryButton';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import useThemeColors from '@/hooks/useThemeColors';
import AppLayout from '@/layouts/AppLayout';
import { useForm } from '@inertiajs/react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface EnrollmentCertificateProps {
    enrolled_courses: { id: number; title: string }[];
    errors?: {
        course_id?: string;
        dni?: string;
        address?: string;
        non_field_errors?: string[];
    };
    csrfToken?: string;
}

const EnrollmentCertificateContent: React.FC<EnrollmentCertificateProps> = ({
    enrolled_courses,
    errors: serverErrors,
    csrfToken,
}) => {
    const { t } = useTranslation();
    const colors = useThemeColors();

    // Using useForm just for form state management, not for submission
    const { data, setData, errors } = useForm<{
        course_id: string;
        dni: string;
        address: string;
    }>({
        course_id: '',
        dni: '',
        address: '',
    });

    const hasCourses = enrolled_courses.length > 0;

    return (
        <AppLayout>
            <PageTitle title="Certificado de Matriculacion" />
            <div className="mx-auto max-w-3xl space-y-8">
                {/* Page Header */}
                <div className="space-y-4">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white sm:text-5xl">
                        {t('enrollmentCertificate.title')}
                    </h1>
                    <p className="text-lg text-gray-700 dark:text-neutral-300 sm:text-xl">
                        {t('enrollmentCertificate.description')}
                    </p>
                </div>

                {/* Form Card */}
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-700 dark:bg-neutral-800 sm:p-8">
                    {serverErrors?.non_field_errors && (
                        <div className="mb-6 rounded-lg bg-red-50 p-4 text-red-800">
                            {serverErrors.non_field_errors.join(', ')}
                        </div>
                    )}

                    <form
                        method="POST"
                        action="/enrollment-certificate/download/"
                        className="space-y-6"
                    >
                        <input
                            type="hidden"
                            name="csrfmiddlewaretoken"
                            value={csrfToken}
                        />
                        {/* Course Field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="course_id"
                                className="block text-base font-medium text-gray-700 dark:text-neutral-300"
                            >
                                {t('enrollmentCertificate.courseLabel')}
                            </label>
                            <select
                                id="course_id"
                                name="course_id"
                                value={data.course_id}
                                onChange={(e) =>
                                    setData('course_id', e.target.value)
                                }
                                disabled={!hasCourses}
                                className={`block w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-gray-900 focus:ring-2 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white ${colors.inputFocus}`}
                            >
                                <option value="">
                                    {hasCourses
                                        ? t('enrollmentCertificate.selectCourse')
                                        : t(
                                              'enrollmentCertificate.noCoursesAvailable'
                                          )}
                                </option>
                                {enrolled_courses.map((course) => (
                                    <option key={course.id} value={course.id}>
                                        {course.title}
                                    </option>
                                ))}
                            </select>
                            {(errors.course_id || serverErrors?.course_id) && (
                                <p className="text-base text-red-600">
                                    {errors.course_id ||
                                        serverErrors?.course_id}
                                </p>
                            )}
                        </div>

                        {/* DNI Field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="dni"
                                className="block text-base font-medium text-gray-700 dark:text-neutral-300"
                            >
                                {t('enrollmentCertificate.dniLabel')}
                            </label>
                            <input
                                type="text"
                                id="dni"
                                name="dni"
                                value={data.dni}
                                onChange={(e) => setData('dni', e.target.value)}
                                placeholder={t(
                                    'enrollmentCertificate.dniPlaceholder'
                                )}
                                className={`block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:outline-none dark:border-neutral-600 dark:bg-neutral-700 dark:text-white dark:placeholder-neutral-400 ${colors.inputFocus}`}
                            />
                            {(errors.dni || serverErrors?.dni) && (
                                <p className="text-base text-red-600">
                                    {errors.dni || serverErrors?.dni}
                                </p>
                            )}
                        </div>

                        {/* Address Field */}
                        <div className="space-y-2">
                            <label
                                htmlFor="address"
                                className="block text-base font-medium text-gray-700 dark:text-neutral-300"
                            >
                                {t('enrollmentCertificate.addressLabel')}
                            </label>
                            <input
                                type="text"
                                id="address"
                                name="address"
                                value={data.address}
                                onChange={(e) =>
                                    setData('address', e.target.value)
                                }
                                placeholder={t(
                                    'enrollmentCertificate.addressPlaceholder'
                                )}
                                className={`block w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-900 placeholder-gray-400 focus:ring-2 focus:outline-none dark:border-neutral-600 dark:bg-neutral-700 dark:text-white dark:placeholder-neutral-400 ${colors.inputFocus}`}
                            />
                            {(errors.address || serverErrors?.address) && (
                                <p className="text-base text-red-600">
                                    {errors.address || serverErrors?.address}
                                </p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <PrimaryButton
                            type="submit"
                            size="md"
                            disabled={!data.course_id}
                        >
                            {t('enrollmentCertificate.submitButton')}
                        </PrimaryButton>
                    </form>
                </div>
            </div>
        </AppLayout>
    );
};

const EnrollmentCertificate: React.FC<EnrollmentCertificateProps> = (props) => (
    <ThemeWrapper>
        <EnrollmentCertificateContent {...props} />
    </ThemeWrapper>
);

export default EnrollmentCertificate;
